// src/context/PermissionsProvider.js
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from './auth';
import { apiService } from '../utils/admin/permissionsApi';
import { getCachedData, setCachedData, clearCache } from '../utils/admin/cacheData';

// Create context
const PermissionsContext = createContext(null);

export const PermissionsProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [routePermissions, setRoutePermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);

  // Cache TTL constant
  const PERMISSIONS_TTL = 5 * 60 * 1000; // 5 minutes

  // Helper function to check if cached data is stale
  const isCacheStale = (cachedData) => {
    if (!cachedData || !cachedData.timestamp) return true;
    const age = Date.now() - cachedData.timestamp;
    return age > PERMISSIONS_TTL;
  };

  // Helper function to update state from permissions data
  const updateStateFromPermissions = useCallback((data, timestamp = new Date()) => {
    setIsSuperAdmin(data.isSuperAdmin);
    setRoutePermissions(data.routes || {});
    setLastFetched(timestamp);
  }, []);

  // Helper function to save permissions to cache
  const saveToCache = useCallback(async (userEmail, data) => {
    try {
      const cacheData = {
        isSuperAdmin: data.isSuperAdmin,
        routes: data.routes || {},
        timestamp: Date.now()
      };
      await setCachedData(userEmail, cacheData);
    } catch (err) {
      console.error('Error saving permissions to cache:', err);
      // Don't throw - caching failure shouldn't break the app
    }
  }, []);

  // Background fetch function (non-blocking, doesn't update loading state)
  const fetchPermissionsInBackground = useCallback(async (userEmail) => {
    try {
      const data = await apiService.getPermissions();
      const timestamp = new Date();
      
      // Update state silently (user can already navigate)
      updateStateFromPermissions(data, timestamp);
      
      // Update cache
      await saveToCache(userEmail, data);
      
      console.log('Background permissions refresh completed');
    } catch (err) {
      console.error('Background permissions fetch failed:', err);
      // Don't update error state - this is background, user already has cached data
    }
  }, [updateStateFromPermissions, saveToCache]);

  // Fetch permissions from the server (with IndexedDB cache support)
  const fetchPermissions = useCallback(async (forceRefresh = false) => {
    // Ensure user email is sanitized for consistency.
    const cleanUserEmail = user?.email?.trim().toLowerCase();

    if (!isAuthenticated || !cleanUserEmail) {
      setIsSuperAdmin(false);
      setRoutePermissions({});
      setLoading(false);
      return;
    }
    
    try {
      setError(null);
      
      // Step 1: Check cache first (unless force refresh)
      if (!forceRefresh) {
        const cachedData = await getCachedData(cleanUserEmail);
        
        if (cachedData && !isCacheStale(cachedData)) {
          // Cache is fresh - use it immediately
          console.log('Using cached permissions');
          const cachedTimestamp = cachedData.timestamp ? new Date(cachedData.timestamp) : new Date();
          updateStateFromPermissions(cachedData, cachedTimestamp);
          setLoading(false);
          
          // Fetch fresh data in background (non-blocking)
          fetchPermissionsInBackground(cleanUserEmail).catch(() => {
            // Silently fail - user already has cached data
          });
          
          return cachedData;
        } else if (cachedData && isCacheStale(cachedData)) {
          // Cache exists but is stale - use it immediately, then refresh
          console.log('Cache is stale, using cached data while fetching fresh');
          const cachedTimestamp = cachedData.timestamp ? new Date(cachedData.timestamp) : new Date();
          updateStateFromPermissions(cachedData, cachedTimestamp);
          setLoading(false);
          
          // Fetch fresh data in background
          fetchPermissionsInBackground(cleanUserEmail).catch(() => {
            // Silently fail - user has stale cached data
          });
          
          return cachedData;
        }
        // No cache or cache is stale - fall through to API fetch
      }
      
      // Step 2: Fetch from API (cache miss, stale cache, or force refresh)
      console.log('Fetching permissions from API');
      setLoading(true);
      
      const data = await apiService.getPermissions();
      
      // Update state
      const timestamp = new Date();
      updateStateFromPermissions(data, timestamp);
      
      // Save to cache
      await saveToCache(cleanUserEmail, data);
      
      return data;
    } catch (err) {
      setError(err.message);
      console.error('Error fetching permissions:', err);
      
      // If API fails, try to use stale cache as fallback
      if (!forceRefresh) {
        const cachedData = await getCachedData(cleanUserEmail);
        if (cachedData) {
          console.log('API failed, using cached permissions as fallback');
          const cachedTimestamp = cachedData.timestamp ? new Date(cachedData.timestamp) : new Date();
          updateStateFromPermissions(cachedData, cachedTimestamp);
          setLoading(false);
          return cachedData;
        }
      }
      
      return null;
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user, updateStateFromPermissions, saveToCache, fetchPermissionsInBackground]);

  // Fetch permissions on authentication change
  useEffect(() => {
    const cleanUserEmail = user?.email?.trim().toLowerCase();
    if (isAuthenticated && cleanUserEmail) {
      console.log('User authenticated, fetching permissions');
      fetchPermissions();
    } else {
      console.log('User not authenticated, clearing permissions');
      setIsSuperAdmin(false);
      setRoutePermissions({});
      setLastFetched(null);
      // Note: We don't clear the IndexedDB cache on logout to allow instant re-login
      // If you want to clear it, uncomment: clearCache(cleanUserEmail);
    }
  }, [fetchPermissions, isAuthenticated, user]);

  // Periodic background refresh of permissions (every 5 minutes)
  useEffect(() => {
    if (!isAuthenticated || !user?.email) return;
    
    const cleanUserEmail = user.email.trim().toLowerCase();
    
    // Set up interval to check and refresh permissions in background
    const refreshInterval = setInterval(() => {
      if (isAuthenticated && lastFetched) {
        const age = Date.now() - lastFetched.getTime();
        if (age > PERMISSIONS_TTL) {
          console.log('Periodic background refresh: permissions expired, refreshing...');
          // Use background fetch (non-blocking)
          fetchPermissionsInBackground(cleanUserEmail).catch(() => {
            // Silently fail - this is background refresh
          });
        }
      }
    }, 5 * 60 * 1000); // Check every 5 minutes
    
    return () => clearInterval(refreshInterval);
  }, [isAuthenticated, lastFetched, user, fetchPermissionsInBackground, PERMISSIONS_TTL]);

  // Check if the user has permission for a route
  const hasPermission = useCallback((route) => {
    // Trigger background refresh if stale (non-blocking)
    if (lastFetched && user?.email) {
      const age = Date.now() - lastFetched.getTime();
      if (age > PERMISSIONS_TTL) {
        console.log('Permissions cache expired (on access), refreshing in background');
        const cleanUserEmail = user.email.trim().toLowerCase();
        fetchPermissionsInBackground(cleanUserEmail).catch(() => {
          // Silently fail - user can still navigate with cached data
        });
      }
    }

    // Super admins always have access
    if (isSuperAdmin) {
      return true;
    }
    
    // Check route permissions. The backend now sends a simple true/false.
    return routePermissions[route] === true;
  }, [isSuperAdmin, routePermissions, lastFetched, user, fetchPermissionsInBackground]);

  // Check if user is Google Ads admin
  const isAdsAdmin = useMemo(() => {
    return isSuperAdmin || routePermissions['admin/ads'] === true;
  }, [isSuperAdmin, routePermissions]);

  // Check if user is GA4 admin
  const isGa4Admin = useMemo(() => {
    return isSuperAdmin || routePermissions['admin/ga4'] === true;
  }, [isSuperAdmin, routePermissions]);

  // Check if user is GTM admin
  const isGtmAdmin = useMemo(() => {
    return isSuperAdmin || routePermissions['admin/gtm'] === true;
  }, [isSuperAdmin, routePermissions]);

  // Other methods using apiService...
  const updatePermissions = useCallback(async (routes) => {
    if (!isSuperAdmin) {
      throw new Error('Only super admins can update permissions');
    }
    
    try {
      setLoading(true);
      const result = await apiService.updatePermissions(routes);
      // Force refresh after update to get latest permissions
      await fetchPermissions(true);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [isSuperAdmin, fetchPermissions]);

  // Create context value
  const permissionsValue = {
    isSuperAdmin,
    isAdsAdmin,
    isGa4Admin,
    isGtmAdmin,
    routePermissions,
    hasPermission,
    updatePermissions,
    refreshPermissions: fetchPermissions,
    loading,
    error,
    lastFetched
  };

  return (
    <PermissionsContext.Provider value={permissionsValue}>
      {children}
    </PermissionsContext.Provider>
  );
};

// Custom hook to use the permissions context
export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  
  return context;
};
